import * as Y from 'yjs';
import {
  TiptapCollabProvider,
  TiptapCollabProviderWebsocket,
} from '@tiptap-pro/provider';
import { generateHTML, generateJSON } from '@tiptap/core';
import { Content, Editor } from '@tiptap/react';
import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { TSessionConfigData } from 'providers/contexts/CosmosContext';
import { isDeepEqual, isNumber } from 'remeda';
import { CollabProviderState } from 'screens/editorial/asset/context/AssetConfigUtilities';
import { CollabUserInfo } from 'screens/editorial/asset/presence/useDocumentPresence';
import { EditorSetupConfig } from 'screens/editorial/asset/utility/getEditorConfigs';
import {
  formatContent,
  getEditorExtensions,
  getEditorProps,
  UseCreateEditorParams,
} from './useCreateEditor';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

function createMockCollabUser(): CollabUserInfo {
  const mockUser = {
    name: 'mockeditor',
    color: '#ABCDEF',
    uuid: '00000000-0000-0000-0000-000000000000',
    email: 'mockeditor@test.com',
    avatar: 'https://www.gravatar.com/avatar/?d=mp',
    runtimeId: 'runtime::00000000-0000-0000-0000-000000000000',
    isReadOnlyMode: false,
    lastActiveIso: new Date().toISOString(),
  };
  return mockUser;
}

/**
 * createMockProvider creates a mock/dummy provider instance
 * that should not try to connect to any real server or websocket instance.
 *
 * Useful for testing or if you want to create a provider instance
 * but do not want to connect to a real server or websocket, for
 * use in cases like createMockEditor where we need a provider instance
 * to be able to setup the correct set of extensions for the editor.
 *
 * Untested, but it may also be possible to use mock instance to basically
 * be able to run our editors always in a y-doc/collab exts mode while still
 * allowing the nxt users users opt out of 'collaboration'.
 *
 * Some of these extensions basically rely on a ydoc to
 * communicate and store updates, parts of them internally
 * rely on the ydoc events (eg: ydoc.on('update',...)
 * and as we use the ydoc  basically like a scratchpad
 * to communicate over the wire, maybe with 'nxt-collaboration' off
 * using a mock provider we could still use those extensions?
 * while not communicating any updates to the tiptap cloud...?
 *
 * eg: snapshot-compare, history, etc.
 * https://tiptap.dev/docs/editor/extensions/functionality/snapshot-compare
 * https://tiptap.dev/docs/editor/extensions/functionality/history
 *
 * Open Source 'hocuspocus' Collaboration Provider:
 * https://github.com/ueberdosis/hocuspocus/tree/main/packages/provider
 *
 * Couldnt find the TiptapCollabProvider there only some older forks on github..
 * search for "export class TiptapCollabProvider extends HocuspocusProvider"
 *
 */

export function createMockProvider(): TiptapCollabProvider {
  const mockUser = createMockCollabUser();
  const mockYdoc = new Y.Doc();
  // tricks to prevent any real connections
  const empty = ''; // also works: '\0' or null

  // Notes:
  // TiptapCollabProvider types mismatch, tiptap claims certain props
  // on the provider but they are actually on the websocket instance
  // https://tiptap.dev/docs/collaboration/provider/integration#set-up-the-provider

  // Important: do not connect to a real instance.
  // the provider 'connect' prop seems to be broken
  // and possibly is ignored as even if you set false
  // in the devtools/cloud logs https://cloud.tiptap.dev/v1/apps/logs
  // you can notice it is attempting to connect anyway
  // with this in mind we will also create a websocket instance
  // and do some tricks that basically send the connection nowhere.
  // basically null out mock websocket and mock provider asap
  // add all props that would make it disconnect or not
  // even attempt to connect to anything automatically

  // setting certain values eg url/baseurl to null, '\0', '' seems to stop it as well
  // if changing please validate no ws mock connection in devtools and cloud logs

  const wsMock = new TiptapCollabProviderWebsocket({
    url: empty,
    baseUrl: empty,
    appId: empty,
    // autoConnect:false is the key guard: the websocket constructor only kicks
    // off a connection (and its retry loop) when shouldConnect is true. Without
    // this, the provider immediately attempts to connect and the 1ms timeout
    // below rejects with an unhandled
    // "Retry timeout (attemptNum: 0, timeout: 1)" (from @lifeomic/attempt via
    // @hocuspocus/provider). Disabling autoConnect stops the attempt entirely.
    autoConnect: false,
    messageReconnectTimeout: 1,
    timeout: 1,
    maxDelay: 1,
    minDelay: 1,
    jitter: false,
    maxAttempts: 1,
    initialDelay: 0,
  });

  // ensure it wont try to autoconnect...
  wsMock.shouldConnect = false;

  // maybe too aggressive, commenting in case needed...
  // wsMock.rejectConnectionAttempt(); wsMock.stopConnectionAttempt();
  // wsMock.disconnect(); wsMock.destroy();

  // create mock provider with nonsense websocket
  const mockProvider = new TiptapCollabProvider({
    url: empty,
    baseUrl: empty,
    appId: empty,
    token: empty,
    name: mockUser.name,
    user: mockUser.name,
    document: mockYdoc,
    forceSyncInterval: false,
    websocketProvider: wsMock,
  });

  // same comments as mock websocket for similar reasons
  // mockProvider.disconnect();
  // mockProvider.destroy();

  return mockProvider;
}

/**
 * The mock/dummy editor is mainly used as a tool in situations where
 * we need to get the editor schema or tiptap/prosemirror content as json
 * or html, or use other editor methods, but do not want to use a real editor.
 * eg. as to not connect to the actual live ydoc or collab provider. Can also
 * be used to setup a dummy editor with some initial content for diff to live purposes.
 *
 * See comments EOF in AssetConfigContextProvider for more info on use cases
 * https://tiptap.dev/docs/editor/core-concepts/schema#get-the-underlying-prosemirror-schema
 */

interface CreateMockEditorParams extends UseCreateEditorParams {
  editorConfig?: EditorSetupConfig;
  sessionConfig?: TSessionConfigData;
  collabProvider?: CollabProviderState;
}

export function createMockEditor({
  editorConfig,
  collabProvider,
  sessionConfig,
  ...restEditorProps
}: CreateMockEditorParams) {
  const hasCollab = Boolean(collabProvider);

  const mockUser = createMockCollabUser();

  const mockProvider = hasCollab ? createMockProvider() : undefined;

  const initialContent = formatContent(editorConfig?.contentForEditor);

  const buildExts = getEditorExtensions({
    editorConfig: editorConfig,
    sessionConfig: sessionConfig,
    collabProvider: mockProvider,
    collabUser: mockUser,
  });

  const buildEditorProps = getEditorProps(editorConfig?.type);

  const isEditable = editorConfig?.editable ?? true;

  const editor = new Editor({
    content: undefined,
    editable: isEditable,
    extensions: buildExts,
    editorProps: buildEditorProps,
    enableContentCheck: false,
    ...restEditorProps,
  });

  // deviation from the useEditor hook and uncontrolled version here:
  // setContent right after it is instantiated before returning...
  // for some reason this was more reliable here than in the
  // onCreate callback or content prop, since the setContent
  // seems to run before returning the editor instance
  // so outside of this function you can do editor.getJSON(), etc
  // asap and it seems to have the content without any delay...

  // mainly noticed this on certain node attrs when done
  // in the onCreate/content prop, like toc-id, etc, were not
  // there on the initial render, it felt like this was returning
  // the editor before extensions finished loading and
  // modifying the content as they initalize. However if we
  // do the e.chain.setcontent(...) ... before the return
  // it all seems to be okay on the first render...

  // mock editor not concerned with the ydoc or collab
  // so we can just run the tiptap init chain setcontent
  try {
    editor
      .chain()
      .setContent(initialContent, {
        emitUpdate: true,
        errorOnInvalidContent: false,
      })
      .setMeta('bloxInitContent', true)
      .run();
  } catch (error) {
    console.error('[createMockEditor]', error);
  }

  return editor;
}

/**
 * Helper utility to convert some Content to
 * a tiptap html string as would be similarly generated
 * on a real editor instance with the same extensions.
 * https://tiptap.dev/docs/editor/api/utilities/html
 *
 * Generally this can be done directly on an editor instance
 * using editor.getHTML() method but in some cases
 * we may need to convert json without a real editor
 * and you may not want to setup a mock editor just for this.
 *
 *
 * Important Note:
 * The extensions matter, including collaboration ones.
 * If incorrect extensions loaded, output will not
 * match what a real editor would produce. For example
 * if you had a comment <span> node in the html, and you did not
 * load the CommentsKit extension, it would be stripped out here.
 *
 * Some nodes/attrs like toc-id always will be auto
 * generated so if using for diff, need to ignore,
 * use it in conjunction with cleanupTiptapHtml().
 *
 * We dont need to connect or setup a real collab provider
 * here, we just need to push the proper ext to the helper
 * so it can get the correct schema from the extension set.
 *
 */

type GenerateTiptapHtmlParams = {
  content?: Content; // eg string / number
  editorConfig?: EditorSetupConfig; // editor config for extensions
  sessionConfig?: TSessionConfigData; // session config for extensions
  mockCollab?: boolean; // set mock collab provider
};

export function generateTiptapHtml({
  content,
  editorConfig,
  sessionConfig,
  mockCollab,
}: GenerateTiptapHtmlParams): string {
  // similar to formatContent() when creating editors
  // need to enforce certain type conversion for tiptap utils
  // if undef or null fallback to '', if obj assume JSONContent type
  let input = formatContent(content) ?? '';
  const inputIsObj = typeof input === 'object' && input !== null;

  // setup to get accurate extension set
  const mockUser = createMockCollabUser();
  const mockProvider = mockCollab ? createMockProvider() : undefined;
  const buildExts = getEditorExtensions({
    editorConfig: editorConfig,
    sessionConfig: sessionConfig,
    collabProvider: mockProvider,
    collabUser: mockUser,
  });

  // generate prosemirror json content data structure (similar editor.getJSON())
  // if the input was an object assume the dev passed JSONContent type...
  const jsonContent = inputIsObj ? input : generateJSON(input, buildExts);

  // feed to the prosemirror json to html utility
  // note: this is not the same as just using the html on record
  // but more a representation of: content -> editor -> getHtml()
  // so it will be similar to 'processed by tiptap' html output.
  const htmlContent = generateHTML(jsonContent, buildExts);

  return htmlContent;
}

type CleanupTiptapHtmlParams = {
  /** Possibly dirty html string to clean. */
  dirty?: string | null;
};

/**
 *  Helper to cleanup the html string via output of editor.getHTML()
 *  stripAttributes from various tiptap html elements
 *  before saving it to the db, diff comparisons, etc...
 */
export function cleanupTiptapHtml({ dirty }: CleanupTiptapHtmlParams) {
  if (!dirty) {
    return dirty; // return as is
  }

  // Create a new DOMParser
  const parser = new DOMParser();

  // Parse the tiptap html string to create an HTMLElement
  // unsanitized editor.getHtml() with toc-ids, attrs, etc
  const doc = parser.parseFromString(dirty, 'text/html');

  // rather than doing doc.documentElement use doc.body insead
  // because the docEl will wrap with <head> and <body>
  // tags to the result, which we do not want in most cases

  // get body of the doc
  const body = doc.body;

  // get all elements in the body
  const allElements = body.querySelectorAll('*');

  // Iterate through each element
  // Remove TipTap Inline Asset attributes so erroneous data is not saved
  allElements.forEach((element, key, parent) => {
    if (
      element.tagName === 'BLOX-INLINE' &&
      element.getAttribute('uuid') === null &&
      element.getAttribute('type') !== 'twitter'
    ) {
      element.setAttribute('placeholder', 'true');
    }
    // Remove data-toc-id attribute created by TipTap Table of Contents
    element.removeAttribute('data-toc-id');
    // Remove client-only Style Guide node hashes (CMSADMIN-2267)
    element.removeAttribute('_hash');
    // CMSADMIN-1397: Remove rel attribute from links
    if (element.tagName === 'A') {
      element.removeAttribute('rel');
    }
  });

  /**
   * Cleanup tiptap v3 trailingNode auto insert empty <p> to end of doc.
   * Sanitizing here in addition to disabling ext. Can cause
   * false positive autosave diff checks and does not represent
   * the content structure as provided by the api record.
   * The API also does a similar cleanup on saves (libtidy5).
   *
   * Removes last empty <p></p> only if other content exists; keeps
   * it if its the only element, to maintain valid doc structure.
   */

  const lastChild = body.lastElementChild;
  if (
    lastChild &&
    lastChild.tagName === 'P' &&
    lastChild.textContent?.trim() === '' &&
    lastChild.children.length === 0 &&
    body.children.length > 1
  ) {
    lastChild.remove();
  }

  // get inner cleaned html
  const cleanHtml = body.innerHTML;

  return cleanHtml;
}

/**
 * Primary use in: AutoSave
 * Previously: "isContentActuallyDirty".
 *
 * Helper function to diff the content of
 * two strings as output by editor.getHTML()
 *
 * Notes:
 * Backend API also does some cleanup on on save to certain fields
 * so its not enough to simply compare "record.field === editor.getHTML()"
 * also the dirty state of react hook form will almost always be dirty
 * since certain prosemirror/tiptap nodes eg inline assets, links etc
 * modify their attrs and other properties during runtime,changing the
 * underlying value of the input.
 *
 * An example of this behavior can be seen in the tiptap v3
 * StarterKit default "trailingNode" extension, inserts a node
 * automatically after the last block node into editor at runtime...
 * https://tiptap.dev/docs/editor/extensions/functionality/starterkit#extensions
 * https://tiptap.dev/docs/editor/extensions/functionality/trailing-node
 *
 * In order to do some form of diffing we need to normalize things.
 * We must take the record.field value and do either:
 *
 * 1) create a mock editor instance to call .getHtml() for the output
 * 2) or, call mock generateTiptapHtml to mock editor HTML output
 *
 * Once you have generated html, as tiptap would provide,
 * run it through our cleanupTiptapHtml function to
 * stripAttributes such as dynamic ids, and other things
 * that are not relevant to the content itself. It is up
 * to you to decide which attributes are meaningful.
 *
 * Then you can do an equality check on the two strings.
 *
 * @param prevHtml Previous Tiptap HTML content
 * @param currHtml Current Tiptap HTML content
 * @returns object containing dirty boolean and cleaned html strings
 */

type CompareTiptapHtmlArgs = {
  prevHtml?: string | null;
  currHtml?: string | null;
};

type CompareTiptapHtmlRtn = {
  /**
   * Dirty is true if the normalized html strings differ.
   * Indicating a real content 'change' exists.
   * False indicates no meaningful change.
   * Note: if both prevHtml and currHtml are null/undefined/empty
   * then dirty will be false as they are considered the same/clean.
   */
  dirty: boolean;
  // Results of the compareTiptapHtml function for convenience.
  prevHtmlClean: string | null | undefined;
  currHtmlClean: string | null | undefined;
};

export function compareTiptapHtml({
  prevHtml,
  currHtml,
}: CompareTiptapHtmlArgs): CompareTiptapHtmlRtn {
  // pre-compare normalize for comparison and
  // handle edge case of null or undefined
  const prev = typeof prevHtml === 'string' ? prevHtml.trim() : '';
  const curr = typeof currHtml === 'string' ? currHtml.trim() : '';

  // if both normalized to empty strings, just consider as clean
  // to handle some odd edge cases like null vs undefined, etc
  if (prev === '' && curr === '') {
    return {
      dirty: false,
      prevHtmlClean: prev,
      currHtmlClean: curr,
    };
  }

  const prevHtmlClean = cleanupTiptapHtml({ dirty: prev });
  const currHtmlClean = cleanupTiptapHtml({ dirty: curr });
  const hasRealChange = !isDeepEqual(prevHtmlClean, currHtmlClean);

  const result = {
    dirty: hasRealChange,
    prevHtmlClean: prevHtmlClean,
    currHtmlClean: currHtmlClean,
  };

  return result;
}

/**
 * --------------------------------------------------
 * Date Comparison Utilities
 * --------------------------------------------------
 * These were created for an older version of collab
 * sync logic that compared marking last update
 * timestamps on the ydoc vs the blox record last update.
 *
 * They are generic enough to still be useful for
 * other purposes so, leaving them here if we need.
 */

/**
 * per date drift adjustments integer seconds (positive = forward, negative = backward)
 * eg: positive will add seconds to time, negative will subtract seconds.
 * eg: { first: 5, second: 0 } adds 5 seconds to the first date only.
 */
type AdjustDriftConfig = { first: number; second: number };

interface CompareDatesParams {
  first?: string | number | Date;
  second?: string | number | Date;
  ifSame?: boolean | string;
  strict?: boolean;
  driftConfig?: AdjustDriftConfig;
}

const DEFAULT_DRIFT_ADJUST: AdjustDriftConfig = { first: 0, second: 0 } as const;

/**
 * Adjust a Dayjs date by a drift (in seconds).
 * - Validates the input drift (must be a finite number)
 * - Rounds using Math.round (2.3 -> 2, -2.6 -> -3)
 * - Applies only if the date is valid and rounded drift is nonzero.
 * - Returns the original Dayjs if invalid or no-op.
 */
export function adjustDrift(d: Dayjs, driftSec?: number): Dayjs {
  try {
    // invalid dates returned as is
    const dateIsValid = d.isValid();
    if (!dateIsValid) {
      console.warn('[adjustDrift] Invalid date, returning:', d);
      return d;
    }

    // If drift adj is nullish, zero, or rounds to zero, no adjustment needed
    const noAdjustment =
      driftSec === null ||
      driftSec === undefined ||
      driftSec === 0 ||
      Math.round(driftSec) === 0;

    // Confirm drift adj is a valid finite number
    const isValidDrift = isNumber(driftSec) && Number.isFinite(driftSec);

    // No adj needed or invalid drift, return as-is.
    if (!isValidDrift || noAdjustment) {
      return d;
    }

    // Should have a valid finite number by here
    const rounded = Math.round(driftSec) ?? 0;

    // Already checked, but just in case...
    if (rounded === 0) {
      return d;
    }

    // Apply the adjustment
    // Positive drift moves the date forward, negative moves it backward
    // eg. +10 seconds = add 10 seconds, -10 seconds = subtract 10 seconds
    // Use Math.abs for subtract to avoid subtracting a negative
    const magnitude = Math.abs(rounded);
    const isPositive = rounded > 0;

    const adjusted = isPositive
      ? d.add(magnitude, 'second') // add time
      : d.subtract(magnitude, 'second'); // remove time

    // re-check for date validity before returning
    // in case the adjustment created an invalid date
    const adjustedValid = adjusted.isValid();
    if (!adjustedValid) {
      console.warn('[adjustDrift] Adjusted date invalid, returning original.');
      return d;
    }

    // Return the adjusted date
    return adjusted;
  } catch (err) {
    console.error('[adjustDrift] Error:', err);
    return d;
  }
}

/**
 * Compare two UTC dates with dayjs in UTC mode.
 *
 * - True if the first date is newer than the second.
 * - False if the second date is newer than the first.
 * - If one date is invalid or falsy, the valid date "wins":
 *   - If only the first is valid, returns true.
 *   - If only the second is valid, returns false.
 *
 * Edge cases:
 *  - If dates are identical returns false.
 *  - If both dates are invalid, throws an error.
 *  - In strict mode, throws an error if either date is invalid.
 *
 * @param first - The first date input (string, number, or Date)
 * @param second - The second date input (string, number, or Date)
 * @param ifSame - A value to return if the dates are identical.
 * @param strict - Strict mode to throw err if either date is invalid.
 * @param driftConfig - Per-side drift adjustments in seconds (positive or negative).
 * example: { first: 0, second: 0 } per default no adjustment.
 * @returns boolean indicating whether the first date is newer than the second or
 * the specified value (defaults to false) if the dates are identical.
 * @throws Error if both dates are invalid or in strict mode if either date is invalid.
 */

export function compareDates({
  first,
  second,
  ifSame = false,
  strict = false,
  driftConfig = DEFAULT_DRIFT_ADJUST,
}: CompareDatesParams) {
  // Use dayjs to parse the provided date inputs
  const date1 = first ? dayjs.utc(first) : dayjs.utc('invalid');
  const date2 = second ? dayjs.utc(second) : dayjs.utc('invalid');

  // Determine validity of each parsed date
  const isDate1Valid = date1.isValid();
  const isDate2Valid = date2.isValid();

  const eitherDateInvalid = !isDate1Valid || !isDate2Valid;
  if (strict && eitherDateInvalid) {
    throw new Error('[compareDates] One or more dates are invalid.');
  }

  // If both dates are invalid, throw an error...
  if (!isDate1Valid && !isDate2Valid) {
    throw new Error('[compareDates] Both dates are invalid.');
  }

  // In our non-strict use case where dates may be missing
  // If one date is invalid and the other is valid
  // then the valid date is the winner...

  // First date wins
  if (isDate1Valid && !isDate2Valid) {
    return true;
  }

  // Second date wins
  if (!isDate1Valid && isDate2Valid) {
    return false;
  }

  // Both valid dayjs objects - apply per date drift adjustments.
  const adj1 = adjustDrift(date1, driftConfig?.first ?? 0);
  const adj2 = adjustDrift(date2, driftConfig?.second ?? 0);

  // If dates are identical, return specified value
  const datesAreIdentical = adj1.isSame(adj2);
  if (datesAreIdentical) {
    return ifSame;
  }

  // compare the adjusted dates
  const compare = adj1.isAfter(adj2);

  return compare;
}
